// packages/sdk/src/assessments.ts
// Static catalog — platform-wide, not tenant-scoped.
// For read-only display; no Edge Function needed.

export type AssessmentCategory = 'selection' | 'development' | 'organizational' | 'specialized';

export type AssessmentBadge =
  | 'selection-grade'
  | 'validated'
  | 'development-only'
  | 'caution'
  | 'organizational'
  | 'not-recommended';

export interface Assessment {
  key: string;
  name: string; // Georgian name
  nameEn: string; // English name
  category: AssessmentCategory;
  subcategory: string;
  badge: AssessmentBadge;
  shortDesc: string; // 1 sentence, Georgian
  fullDesc: string; // full description, Georgian
  duration: string;
  languages: string[];
  forWho: string;
  publisher?: string;
  citation: string;
  warnings: string[];
  canUseInSelection: boolean;
}

export const ASSESSMENTS_CATALOG: Assessment[] = [
  // ─── SELECTION / COGNITIVE ────────────────────────────────────────────
  {
    key: 'raven_spm',
    name: "Raven's Progressive Matrices (SPM)",
    nameEn: "Raven's Progressive Matrices",
    category: 'selection',
    subcategory: 'cognitive',
    badge: 'selection-grade',
    shortDesc: 'ლოგიკურ-აბსტრაქტული აზროვნების ოქროს სტანდარტი — ენისგან თავისუფალი (culture-fair)',
    fullDesc:
      "Raven's Progressive Matrices ფიგურულ-არავერბალური IQ ტესტია. ენა არ ჭირდება, საქართველოშიც ნორმალურად მუშაობს.",
    duration: '40 წთ',
    languages: ['არ ჭირდება'],
    forWho: 'ყველა პოზიცია — განსაკუთრებით ანალიტიკური სამუშაო',
    publisher: 'Pearson',
    citation: 'Raven, J. (2000). Cognitive Psychology, 41(1), 1-48.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'shl_verify_numerical',
    name: 'SHL Verify Numerical Reasoning',
    nameEn: 'SHL Verify Numerical Reasoning',
    category: 'selection',
    subcategory: 'cognitive',
    badge: 'selection-grade',
    shortDesc: 'რიცხვითი ლოგიკა — გრაფიკები, ცხრილები, პროცენტები',
    fullDesc: 'SHL-ის Verify ბატარეიდან — adaptive numerical reasoning ტესტი.',
    duration: '25 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'ფინანსები, ბუღალტერია, ანალიტიკა, მენეჯმენტი',
    publisher: 'SHL',
    citation: 'Bartram, D. (2005). Journal of Applied Psychology, 90(6), 1185-1203.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'shl_verify_verbal',
    name: 'SHL Verify Verbal Reasoning',
    nameEn: 'SHL Verify Verbal Reasoning',
    category: 'selection',
    subcategory: 'cognitive',
    badge: 'selection-grade',
    shortDesc: 'ვერბალური ლოგიკა და ტექსტის გაგება',
    fullDesc: 'SHL-ის verbal reasoning ტესტი — ტექსტიდან ლოგიკური დასკვნის გამოტანა.',
    duration: '25 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'ნებისმიერი knowledge worker',
    publisher: 'SHL',
    citation: 'Bartram, D. (2005). Journal of Applied Psychology, 90(6), 1185-1203.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'cubiks_logiks',
    name: 'Cubiks Logiks General',
    nameEn: 'Cubiks Logiks General',
    category: 'selection',
    subcategory: 'cognitive',
    badge: 'selection-grade',
    shortDesc: 'ვერბალური + რიცხვითი + აბსტრაქტული აზროვნება ერთად — adaptive',
    fullDesc: '30 წუთიანი adaptive ტესტი, რომელიც აერთიანებს სამივე reasoning-ის ტიპს.',
    duration: '30 წთ',
    languages: ['ინგლისური'],
    forWho: 'ყველა პოზიცია',
    publisher: 'Cubiks (PSI Services)',
    citation: 'PSI Services (2020). Cubiks Logiks Technical Manual.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'watson_glaser',
    name: 'Watson-Glaser Critical Thinking (CTA-III)',
    nameEn: 'Watson-Glaser Critical Thinking Appraisal',
    category: 'selection',
    subcategory: 'cognitive',
    badge: 'selection-grade',
    shortDesc: 'კრიტიკული აზროვნების ოქროს სტანდარტი — განსაკუთრებით მენეჯერებისთვის',
    fullDesc:
      '90+ წლიანი ისტორიის მქონე ტესტი. ზომავს inference, recognition of assumptions, deduction, interpretation, evaluation of arguments.',
    duration: '30 წთ',
    languages: ['ინგლისური'],
    forWho: 'მენეჯერები, იურისტები, ანალიტიკოსები, სენიორი პოზიციები',
    publisher: 'Pearson',
    citation: 'Watson, G. & Glaser, E. (2010). Watson-Glaser II Technical Manual. Pearson.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'icar_cognitive',
    name: 'ICAR Cognitive Battery',
    nameEn: 'International Cognitive Ability Resource (ICAR)',
    category: 'selection',
    subcategory: 'cognitive',
    badge: 'caution',
    shortDesc: 'Public Domain კოგნიტური ბატარეა — სელექციის მხარდაჭერა სიფრთხილით',
    fullDesc:
      'ICAR16 / ICAR60 — ლოგიკური მსჯელობა, სივრცული, ვერბალური, processing speed. Public Domain. სასელექციოდ დასაშვებია მხოლოდ job relevance-ის დადასტურებით, ადამიანური გადახედვით და validation log-ით. ავტომატური rejection-ი აკრძალულია.',
    duration: '20-40 წთ',
    languages: ['ინგლისური'],
    forWho: 'კოგნიტური სელექციის მხარდაჭერა — ადამიანური გადახედვა სავალდებულოა',
    publisher: 'ICAR (Public Domain)',
    citation:
      'Condon, D. M., & Revelle, W. (2014). The International Cognitive Ability Resource. Intelligence, 43, 52-64.',
    warnings: [
      'სავალდებულოა job relevance-ის წინასწარ დადასტურება',
      'ადამიანური გადახედვა — ავტომატური decision-ი აკრძალულია',
      'Adverse impact რისკი — validation log სავალდებულოა',
    ],
    canUseInSelection: true,
  },
  // ─── SELECTION / PERSONALITY ──────────────────────────────────────────
  {
    key: 'hogan_hpi',
    name: 'Hogan Personality Inventory (HPI)',
    nameEn: 'Hogan Personality Inventory',
    category: 'selection',
    subcategory: 'personality',
    badge: 'selection-grade',
    shortDesc: 'Big Five-ის სამუშაო კონტექსტში სელექციონირებული ვერსია — fake-რეზისტენტული',
    fullDesc:
      'Hogan-ის bright side personality assessment, სასელექციოდ, social desirability კონტროლით.',
    duration: '20 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'ყველა პოზიცია, განსაკუთრებით leadership',
    publisher: 'Hogan Assessment Systems',
    citation: 'Hogan, R. & Hogan, J. (2007). HPI Manual (3rd ed.). Hogan Assessment Systems.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'hogan_hds',
    name: 'Hogan Development Survey (HDS)',
    nameEn: 'Hogan Development Survey',
    category: 'selection',
    subcategory: 'personality',
    badge: 'selection-grade',
    shortDesc: 'სტრესის ქვეშ რა „dark side" ტენდენციები იჩენს თავს',
    fullDesc: 'Dark side personality — სტრესირებული ადამიანის derailing ქცევა.',
    duration: '20 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'Leadership, high-stakes პოზიციები',
    publisher: 'Hogan Assessment Systems',
    citation: 'Hogan, R. & Hogan, J. (2009). HDS Manual. Hogan Assessment Systems.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'shl_opq32',
    name: 'SHL OPQ32',
    nameEn: 'SHL Occupational Personality Questionnaire 32',
    category: 'selection',
    subcategory: 'personality',
    badge: 'selection-grade',
    shortDesc: '32 პიროვნული მახასიათებელი სამუშაო კონტექსტში',
    fullDesc: 'SHL-ის ფლაგმანი პიროვნული ტესტი. დეტალური job-related personality profile.',
    duration: '25 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'ყველა professional პოზიცია',
    publisher: 'SHL',
    citation: 'Bartram, D. (2009). The OPQ32 Technical Manual. SHL Group.',
    warnings: [],
    canUseInSelection: true,
  },
  // ─── SELECTION / INTEGRITY ────────────────────────────────────────────
  {
    key: 'reid_report',
    name: 'Reid Report (Integrity Test)',
    nameEn: 'Reid Report',
    category: 'selection',
    subcategory: 'integrity',
    badge: 'selection-grade',
    shortDesc: 'პატიოსნებისა და counterproductive behavior-ის რისკის შეფასება',
    fullDesc: 'Integrity test — ქურდობის, თაღლითობის, workplace deviance-ის რისკი.',
    duration: '20 წთ',
    languages: ['ინგლისური'],
    forWho: 'ბანკები, ფინანსური პოზიციები, რითეილი, კასირები',
    publisher: 'Pearson',
    citation: 'Ones, D. S. et al. (1993). Journal of Applied Psychology, 78(4), 679-703.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'stanton_survey',
    name: 'Stanton Survey',
    nameEn: 'Stanton Survey',
    category: 'selection',
    subcategory: 'integrity',
    badge: 'selection-grade',
    shortDesc: 'პატიოსნების ალტერნატიული ტესტი',
    fullDesc: 'Reid Report-ის ალტერნატივა.',
    duration: '20 წთ',
    languages: ['ინგლისური'],
    forWho: 'რითეილი, ფინანსური სექტორი',
    publisher: 'Pinkerton',
    citation: 'Sackett, P. R. & Wanek, J. E. (1996). Personnel Psychology, 49(4), 787-829.',
    warnings: [],
    canUseInSelection: true,
  },
  // ─── SELECTION / PRACTICAL ────────────────────────────────────────────
  {
    key: 'mos_excel',
    name: 'Microsoft Office Specialist (MOS) Excel',
    nameEn: 'MOS Excel Certification',
    category: 'selection',
    subcategory: 'practical',
    badge: 'validated',
    shortDesc: 'რეალური Excel-ის ცოდნის ოფიციალური Microsoft სერთიფიკატი',
    fullDesc: 'ცოცხალი Excel-ის გარემოში ფორმულების, Pivot Tables-ის, VLOOKUP-ის გამოყენება.',
    duration: '50 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'ფინანსისტი, ანალიტიკოსი, ბუღალტერი, ადმინისტრატორი',
    publisher: 'Microsoft',
    citation: 'Microsoft Learn (2024). MOS: Excel Associate Certification.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'mos_word',
    name: 'MOS Word',
    nameEn: 'MOS Word Certification',
    category: 'selection',
    subcategory: 'practical',
    badge: 'validated',
    shortDesc: 'Word-ის პროფესიული ცოდნა',
    fullDesc: 'დოკუმენტების ფორმატირება, mail merge, references, advanced features.',
    duration: '50 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'ადმინისტრატორი, ასისტენტი, იურისტი',
    publisher: 'Microsoft',
    citation: 'Microsoft Learn (2024). MOS: Word Associate Certification.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'mos_powerpoint',
    name: 'MOS PowerPoint',
    nameEn: 'MOS PowerPoint Certification',
    category: 'selection',
    subcategory: 'practical',
    badge: 'validated',
    shortDesc: 'PowerPoint-ის პროფესიული ცოდნა',
    fullDesc: 'პრეზენტაციების შექმნა, ანიმაცია, master slides, transitions.',
    duration: '50 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'მარკეტოლოგი, კონსულტანტი, მენეჯერი',
    publisher: 'Microsoft',
    citation: 'Microsoft Learn (2024). MOS: PowerPoint Certification.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'hackerrank',
    name: 'HackerRank Coding Tests',
    nameEn: 'HackerRank for Work',
    category: 'selection',
    subcategory: 'practical',
    badge: 'selection-grade',
    shortDesc: 'პროგრამისტებისთვის — coding ცოდნის შეფასება',
    fullDesc: 'Real-world coding challenges 30+ ენაში. Anti-cheat-ით.',
    duration: '60-90 წთ',
    languages: ['ინგლისური'],
    forWho: 'Software Engineers, Data Engineers, DevOps',
    publisher: 'HackerRank',
    citation: 'HackerRank (2024). Skills Assessment Methodology.',
    warnings: [],
    canUseInSelection: true,
  },
  // ─── SELECTION / LANGUAGE ─────────────────────────────────────────────
  {
    key: 'linguaskill',
    name: 'Linguaskill (Cambridge English)',
    nameEn: 'Linguaskill',
    category: 'selection',
    subcategory: 'language',
    badge: 'selection-grade',
    shortDesc: 'ინგლისური — Reading, Listening, Writing, Speaking ერთად, A1–C2',
    fullDesc: 'Cambridge-ის თანამედროვე adaptive online ტესტი. ოთხივე უნარი იზომება.',
    duration: '60-85 წთ',
    languages: ['ინგლისური'],
    forWho: 'ნებისმიერი პოზიცია სადაც ინგლისური საჭიროა',
    publisher: 'Cambridge Assessment English',
    citation: 'Cambridge Assessment English (2019). Linguaskill: Validation Report.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'aptis',
    name: 'Aptis (British Council)',
    nameEn: 'Aptis',
    category: 'selection',
    subcategory: 'language',
    badge: 'selection-grade',
    shortDesc: 'ინგლისური — ოთხივე უნარი, British Council-ის ტესტი',
    fullDesc: 'Linguaskill-ის ალტერნატივა, British Council-ისგან.',
    duration: '75 წთ',
    languages: ['ინგლისური'],
    forWho: 'ნებისმიერი პოზიცია სადაც ინგლისური საჭიროა',
    publisher: 'British Council',
    citation: "O'Sullivan, B. (2015). Aptis Test Development Approach. British Council.",
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'toeic',
    name: 'TOEIC',
    nameEn: 'Test of English for International Communication',
    category: 'selection',
    subcategory: 'language',
    badge: 'validated',
    shortDesc: 'ბიზნეს ინგლისურის სტანდარტი — Listening + Reading',
    fullDesc: 'ETS-ის TOEIC. კვლავ აღიარებულია მსოფლიოში.',
    duration: '60 წთ',
    languages: ['ინგლისური'],
    forWho: 'კორპორაციული ბიზნეს კონტექსტი',
    publisher: 'ETS',
    citation: 'ETS (2020). TOEIC Listening and Reading: Technical Manual.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'torfl',
    name: 'TORFL (ТРКИ)',
    nameEn: 'Test of Russian as a Foreign Language',
    category: 'selection',
    subcategory: 'language',
    badge: 'selection-grade',
    shortDesc: 'რუსული ენის ერთადერთი ოფიციალური საერთაშორისო ტესტი',
    fullDesc: 'ОФициальный TORFL — A1-დან C2-მდე.',
    duration: '60 წთ',
    languages: ['რუსული'],
    forWho: 'პოზიციები სადაც რუსული საჭიროა',
    publisher: 'Pushkin State Russian Language Institute',
    citation: 'Государственный институт русского языка им. А.С. Пушкина (2022).',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'georgian_writing',
    name: 'ქართული წერითი ტესტი (StepUp)',
    nameEn: 'Georgian Writing Test',
    category: 'selection',
    subcategory: 'language',
    badge: 'validated',
    shortDesc: 'ქართული გრამატიკა, სტილი, წერითი უნარი',
    fullDesc: 'StepUp-ის შინაგანი ტესტი — ქართული საერთაშორისო ოფიციალური ტესტი არ არსებობს.',
    duration: '30 წთ',
    languages: ['ქართული'],
    forWho: 'გაზეთები, საჯარო სამსახური, კონტენტ-ჟანრის პოზიციები',
    publisher: 'StepUp HR',
    citation: 'StepUp internal validation (2025).',
    warnings: [],
    canUseInSelection: true,
  },
  // ─── DEVELOPMENT ──────────────────────────────────────────────────────
  {
    key: 'hexaco',
    name: 'HEXACO-PI-R',
    nameEn: 'HEXACO Personality Inventory Revised',
    category: 'development',
    subcategory: 'self-awareness',
    badge: 'development-only',
    shortDesc: '6-ფაქტორიანი პიროვნების მოდელი (Big Five + Honesty-Humility)',
    fullDesc: 'მეცნიერულად მაღალხარისხიანი ინსტრუმენტი, მაგრამ selection-ში fake-ადვილია.',
    duration: '20-25 წთ',
    languages: ['ინგლისური'],
    forWho: 'თვითშემეცნება, coaching, team-building',
    publisher: 'Free academic use',
    citation:
      'Ashton, M. C., & Lee, K. (2007). Personality and Social Psychology Review, 11(2), 150-166.',
    warnings: ['NOT FOR SELECTION — fake-ადვილია', 'ლოკალური ვალიდაცია საქართველოში არ არსებობს'],
    canUseInSelection: false,
  },
  {
    key: 'big_five_neo',
    name: 'Big Five (NEO-PI-3 / IPIP)',
    nameEn: 'Big Five Personality (NEO-PI-3)',
    category: 'development',
    subcategory: 'self-awareness',
    badge: 'development-only',
    shortDesc: 'კლასიკური 5-ფაქტორიანი პიროვნების მოდელი — აკადემიური ვერსია',
    fullDesc: 'Big Five-ის აკადემიური ვერსია. Selection-ისთვის Hogan/SHL ჯობია.',
    duration: '30-40 წთ',
    languages: ['ინგლისური'],
    forWho: 'თვითშემეცნება, coaching',
    publisher: 'PAR / IPIP (free)',
    citation: 'Costa, P. T., & McCrae, R. R. (1992). NEO PI-R Manual. PAR.',
    warnings: ['NOT FOR SELECTION — fake-ადვილია', 'Hogan HPI-ს გამოყენება selection-ში სჯობს'],
    canUseInSelection: false,
  },
  {
    key: 'big_five_ipip',
    name: 'Big Five Personality Profile (IPIP)',
    nameEn: 'Big Five Personality Profile — IPIP',
    category: 'development',
    subcategory: 'self-awareness',
    badge: 'development-only',
    shortDesc:
      'Goldberg-ის IPIP item bank — Public Domain, Development Only / ინტერვიუს მხარდაჭერა',
    fullDesc:
      'IPIP-ზე დაფუძნებული Big Five ინვენტარი. განვითარებისა და ინტერვიუს მხარდასაჭერად. ავტომატური rejection-ი მკაცრად აკრძალულია. Public Domain ლიცენზია — ვალიდაცია ჩატარებელია ადგილობრივ სამიზნე ჯგუფზე.',
    duration: '10-15 წთ',
    languages: ['ინგლისური'],
    forWho: 'თვითშემეცნება, coaching, ინტერვიუს მხარდაჭერა',
    publisher: 'IPIP (Public Domain)',
    citation:
      'Goldberg, L. R. (1999). A broad-bandwidth, public-domain, personality inventory. In I. Mervielde et al. (Eds.), Personality Psychology in Europe, Vol. 7, pp. 7-28.',
    warnings: [
      'Development Only — ავტომატური rejection-ისთვის გამოყენება აკრძალულია',
      'Fake-ადვილია სასელექციო კონტექსტში',
    ],
    canUseInSelection: false,
  },
  {
    key: 'hexaco_ipip',
    name: 'HEXACO Personality Profile (IPIP-based)',
    nameEn: 'HEXACO Personality Profile — IPIP',
    category: 'development',
    subcategory: 'self-awareness',
    badge: 'caution',
    shortDesc: 'HEXACO 6-ფაქტორიანი მოდელი IPIP item bank-ზე — Advisory only',
    fullDesc:
      'IPIP-HEXACO-PI ვერსია — Honesty-Humility, Emotionality, Extraversion, Agreeableness, Conscientiousness, Openness. Development + Reliability Flag. გამოყენება მხოლოდ advisory კონტექსტში. Public Domain.',
    duration: '15-20 წთ',
    languages: ['ინგლისური'],
    forWho: 'განვითარება, coaching, advisory HR შეფასება',
    publisher: 'IPIP–HEXACO (Public Domain)',
    citation:
      'Ashton, M. C., & Lee, K. (2007). IPIP representation of the HEXACO model. Personality and Individual Differences, 43(6), 1569-1577.',
    warnings: [
      'Advisory Only — სასელექციოდ გამოყენება აკრძალულია',
      'Reliability flag — ლოკალური ვალიდაცია არარსებობს',
    ],
    canUseInSelection: false,
  },
  {
    key: 'mbti',
    name: 'MBTI / 16Personalities',
    nameEn: 'Myers-Briggs Type Indicator',
    category: 'development',
    subcategory: 'self-awareness',
    badge: 'development-only',
    shortDesc: '16-ტიპიანი პიროვნების ტიპოლოგია — მხოლოდ team-building-ში',
    fullDesc: 'გავრცელებული, მაგრამ test-retest reliability სუსტი — 5 კვირაში 50% ცვლის ტიპს.',
    duration: '20-30 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'Team-building, თვითშემეცნება — NOT for hiring',
    publisher: 'The Myers-Briggs Company',
    citation: 'Pittenger, D. J. (2005). Consulting Psychology Journal, 57(3), 210-221.',
    warnings: [
      'NOT FOR SELECTION — გამომქვეყნებელიც კი ოფიციალურად ამბობს „not for hiring decisions"',
      'Test-retest reliability სუსტი',
    ],
    canUseInSelection: false,
  },
  {
    key: 'disc',
    name: 'DISC Assessment',
    nameEn: 'DISC Behavioral Assessment',
    category: 'development',
    subcategory: 'self-awareness',
    badge: 'development-only',
    shortDesc: 'ქცევითი სტილი — Dominance, Influence, Steadiness, Conscientiousness',
    fullDesc: 'ბიზნესში ფართოდ გამოყენებადი, მაგრამ peer-reviewed ვალიდაცია სუსტია.',
    duration: '10-15 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'Team-building, კომუნიკაციის სტილის შეფასება',
    publisher: 'Multiple vendors',
    citation:
      'Jones, C. S., & Hartley, N. T. (2013). American Journal of Business Education, 6(4), 459-470.',
    warnings: ['NOT FOR SELECTION — სამეცნიერო ვალიდაცია სუსტია'],
    canUseInSelection: false,
  },
  {
    key: 'lpi',
    name: 'Leadership Practices Inventory (LPI)',
    nameEn: 'Leadership Practices Inventory',
    category: 'development',
    subcategory: 'leadership',
    badge: 'development-only',
    shortDesc: 'Kouzes & Posner-ის 5 ლიდერული პრაქტიკა',
    fullDesc:
      'Model the Way, Inspire Shared Vision, Challenge the Process, Enable Others, Encourage the Heart.',
    duration: '15 წთ',
    languages: ['ინგლისური'],
    forWho: 'Leadership development, manager coaching',
    publisher: 'Wiley',
    citation: 'Kouzes, J. M., & Posner, B. Z. (2017). The Leadership Challenge (6th ed.). Wiley.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'msceit',
    name: 'MSCEIT',
    nameEn: 'Mayer-Salovey-Caruso Emotional Intelligence Test',
    category: 'development',
    subcategory: 'emotional-intelligence',
    badge: 'development-only',
    shortDesc: 'ემოციური ინტელექტის ability-based ტესტი',
    fullDesc: 'Emotional intelligence-ის გასაზომად ერთ-ერთი ყველაზე ვალიდირებული ინსტრუმენტი.',
    duration: '30-45 წთ',
    languages: ['ინგლისური'],
    forWho: 'Leadership development, coaching',
    publisher: 'MHS Assessments',
    citation: 'Mayer, J. D. et al. (2008). American Psychologist, 63(6), 503-517.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'eq_i',
    name: 'EQ-i 2.0',
    nameEn: 'Emotional Quotient Inventory 2.0',
    category: 'development',
    subcategory: 'emotional-intelligence',
    badge: 'development-only',
    shortDesc: 'Bar-On-ის EI მოდელი',
    fullDesc: 'Self-report EI ტესტი — 5 კომპოზიტი, 15 sub-scale.',
    duration: '20-30 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'Leadership development, HR coaching',
    publisher: 'MHS Assessments',
    citation: 'Bar-On, R. (2006). Psicothema, 18(supl), 13-25.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'custom_workstyle',
    name: 'Custom Workstyle Indicator (StepUp)',
    nameEn: 'Custom Workstyle Indicator',
    category: 'development',
    subcategory: 'self-awareness',
    badge: 'development-only',
    shortDesc: 'სამუშაო სტილი და კომუნიკაციის პრეფერენცია — Development Only',
    fullDesc:
      'StepUp HR-ის ორიგინალური item bank. ზომავს სამუშაო სტილს, კომუნიკაციის პრეფერენციებს. გამოყენება მხოლოდ განვითარებისთვის — ranking, rejection, suitability score მკაცრად აკრძალულია.',
    duration: '10-15 წთ',
    languages: ['ქართული', 'ინგლისური'],
    forWho: 'თვითშემეცნება, team-building, coaching',
    publisher: 'StepUp HR (own item bank)',
    citation: 'StepUp HR internal validation (2026).',
    warnings: [
      'Development Only — ranking, rejection, suitability score-ისთვის გამოყენება აკრძალულია',
    ],
    canUseInSelection: false,
  },
  // ─── ORGANIZATIONAL ───────────────────────────────────────────────────
  {
    key: 'gallup_q12',
    name: 'Gallup Q12',
    nameEn: 'Gallup Q12 Engagement Survey',
    category: 'organizational',
    subcategory: 'engagement',
    badge: 'organizational',
    shortDesc: 'თანამშრომლების ჩართულობის #1 ტესტი მსოფლიოში',
    fullDesc: '12 კითხვიანი engagement survey, 100,000+ კომპანიაში გამოყენებული.',
    duration: '5 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'მთელი კომპანია, ყოველწლიურად',
    publisher: 'Gallup',
    citation: 'Harter, J. K. et al. (2002). Journal of Applied Psychology, 87(2), 268-279.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'uwes',
    name: 'Utrecht Work Engagement Scale (UWES)',
    nameEn: 'Utrecht Work Engagement Scale',
    category: 'organizational',
    subcategory: 'engagement',
    badge: 'organizational',
    shortDesc: 'Vigor, Dedication, Absorption — სამუშაოსადმი ენერგია',
    fullDesc: 'აკადემიური engagement ტესტი — უფასოა, 30+ ენაზე ვალიდირებული.',
    duration: '5-10 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'მთელი კომპანია',
    publisher: 'Free academic use',
    citation:
      'Schaufeli, W. B., & Bakker, A. B. (2004). Journal of Organizational Behavior, 25(3), 293-315.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'enps',
    name: 'Employee Net Promoter Score (eNPS)',
    nameEn: 'Employee Net Promoter Score',
    category: 'organizational',
    subcategory: 'engagement',
    badge: 'organizational',
    shortDesc: '1 კითხვა — „რა ალბათობით ურჩევდი მეგობარს ჩვენთან მუშაობას?"',
    fullDesc: 'სწრაფი pulse check, კვარტალურად. 0-10 სკალა.',
    duration: '30 წამი',
    languages: ['ნებისმიერი'],
    forWho: 'მთელი კომპანია, კვარტალურად',
    publisher: 'Free methodology',
    citation: 'Reichheld, F. F. (2003). Harvard Business Review, 81(12), 46-54.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'mbi',
    name: 'Maslach Burnout Inventory (MBI)',
    nameEn: 'Maslach Burnout Inventory',
    category: 'organizational',
    subcategory: 'burnout',
    badge: 'organizational',
    shortDesc: 'გადაწვის (burnout) რისკის ოქროს სტანდარტი',
    fullDesc: 'Emotional exhaustion, cynicism, professional efficacy.',
    duration: '10 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'ჯანდაცვა, განათლება, IT, ნებისმიერი high-stress სექტორი',
    publisher: 'Mind Garden',
    citation: 'Maslach, C. et al. (2001). Annual Review of Psychology, 52(1), 397-422.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'cbi',
    name: 'Copenhagen Burnout Inventory (CBI)',
    nameEn: 'Copenhagen Burnout Inventory',
    category: 'organizational',
    subcategory: 'burnout',
    badge: 'organizational',
    shortDesc: 'MBI-ის თანამედროვე ალტერნატივა',
    fullDesc: 'Personal, work-related, client-related burnout.',
    duration: '10 წთ',
    languages: ['ინგლისური'],
    forWho: 'ნებისმიერი სექტორი',
    publisher: 'Free academic use',
    citation: 'Kristensen, T. S. et al. (2005). Work & Stress, 19(3), 192-207.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'jss',
    name: 'Job Satisfaction Survey (JSS)',
    nameEn: 'Job Satisfaction Survey',
    category: 'organizational',
    subcategory: 'satisfaction',
    badge: 'organizational',
    shortDesc: '9 ფაქტორი — ხელფასი, წინსვლა, supervisor, benefits...',
    fullDesc: 'Spector-ის 36-კითხვიანი survey, უფასოა.',
    duration: '10 წთ',
    languages: ['ინგლისური'],
    forWho: 'მთელი კომპანია, წელიწადში 1-ჯერ',
    publisher: 'Free academic use',
    citation: 'Spector, P. E. (1985). American Journal of Community Psychology, 13(6), 693-713.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'msq',
    name: 'Minnesota Satisfaction Questionnaire (MSQ)',
    nameEn: 'Minnesota Satisfaction Questionnaire',
    category: 'organizational',
    subcategory: 'satisfaction',
    badge: 'organizational',
    shortDesc: 'Long form: 100 კითხვა / Short form: 20 კითხვა',
    fullDesc: 'კმაყოფილების კლასიკური ტესტი 60+ წლიანი ისტორიით.',
    duration: '15-20 წთ',
    languages: ['ინგლისური'],
    forWho: 'მთელი კომპანია',
    publisher: 'University of Minnesota',
    citation: 'Weiss, D. J. et al. (1967). MSQ Manual. University of Minnesota.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'ocai',
    name: 'Organizational Culture Assessment (OCAI)',
    nameEn: 'Organizational Culture Assessment Instrument',
    category: 'organizational',
    subcategory: 'culture',
    badge: 'organizational',
    shortDesc: 'კომპანიის კულტურის ტიპი — Clan / Adhocracy / Market / Hierarchy',
    fullDesc: 'Cameron & Quinn-ის Competing Values Framework-ზე დაფუძნებული.',
    duration: '15-20 წთ',
    languages: ['ინგლისური'],
    forWho: 'კომპანიის კულტურის შეფასება, change management',
    publisher: 'Free with attribution',
    citation:
      'Cameron, K. S., & Quinn, R. E. (2011). Diagnosing Organizational Culture (3rd ed.). Jossey-Bass.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'denison',
    name: 'Denison Organizational Culture Survey',
    nameEn: 'Denison Organizational Culture Survey',
    category: 'organizational',
    subcategory: 'culture',
    badge: 'organizational',
    shortDesc: 'კულტურა + ბიზნეს შესრულების კავშირი',
    fullDesc: '4 ფაქტორი: Mission, Adaptability, Involvement, Consistency.',
    duration: '20 წთ',
    languages: ['ინგლისური'],
    forWho: 'სტრატეგიული change, M&A კონტექსტი',
    publisher: 'Denison Consulting',
    citation: 'Denison, D. R., & Mishra, A. K. (1995). Organization Science, 6(2), 204-223.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'psych_safety',
    name: 'Psychological Safety Scale (Edmondson)',
    nameEn: 'Team Psychological Safety Scale',
    category: 'organizational',
    subcategory: 'team-dynamics',
    badge: 'organizational',
    shortDesc: 'გუნდში „უსაფრთხოა შეცდომაზე ლაპარაკი" — Google-ის #1 team factor',
    fullDesc:
      '7 კითხვა. Google-ის Project Aristotle-მა დაადგინა — ეს არის #1 ფაქტორი ეფექტური გუნდისთვის.',
    duration: '5 წთ',
    languages: ['ინგლისური'],
    forWho: 'ყველა გუნდი, 6 თვეში 1-ჯერ',
    publisher: 'Free academic use',
    citation: 'Edmondson, A. (1999). Administrative Science Quarterly, 44(2), 350-383.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'team_diagnostic',
    name: 'Team Diagnostic Survey',
    nameEn: 'Team Diagnostic Survey',
    category: 'organizational',
    subcategory: 'team-dynamics',
    badge: 'organizational',
    shortDesc: 'გუნდის ეფექტურობის სრული შეფასება',
    fullDesc: 'Hackman-ის 6 conditions for team effectiveness.',
    duration: '20-30 წთ',
    languages: ['ინგლისური'],
    forWho: 'ფუნქციონალური გუნდები',
    publisher: 'Team Diagnostics LLC',
    citation: 'Wageman, R. et al. (2005). Journal of Applied Behavioral Science, 41(4), 373-398.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'lmx_7',
    name: 'Leader-Member Exchange (LMX-7)',
    nameEn: 'Leader-Member Exchange Questionnaire',
    category: 'organizational',
    subcategory: 'team-dynamics',
    badge: 'organizational',
    shortDesc: 'მენეჯერი ↔ თანამშრომელი ურთიერთობის ხარისხი',
    fullDesc: '7 კითხვა, 3 წუთი. მენეჯერული ეფექტურობის შეფასება.',
    duration: '3 წთ',
    languages: ['ინგლისური'],
    forWho: 'მენეჯერები და მათი გუნდები',
    publisher: 'Free academic use',
    citation: 'Graen, G. B., & Uhl-Bien, M. (1995). The Leadership Quarterly, 6(2), 219-247.',
    warnings: [],
    canUseInSelection: false,
  },
  {
    key: 'feedback_360',
    name: '360-Degree Feedback',
    nameEn: '360-Degree Feedback',
    category: 'organizational',
    subcategory: '360',
    badge: 'organizational',
    shortDesc: 'შეფასება სუპერვაიზერისგან, peer-ებისგან, ქვეშევრდომებისგან',
    fullDesc: 'Leadership development-ისთვის ოქროს სტანდარტი.',
    duration: '15 წთ თითო შემფასებლისთვის',
    languages: ['ინგლისური', 'ქართული'],
    forWho: 'Leadership, manager development',
    publisher: 'Multiple',
    citation: 'Bracken, D. W. et al. (2001). The Handbook of Multisource Feedback. Jossey-Bass.',
    warnings: [],
    canUseInSelection: false,
  },
  // ─── SPECIALIZED ──────────────────────────────────────────────────────
  {
    key: 'spi',
    name: 'Sales Performance Inventory (SPI)',
    nameEn: 'Sales Performance Inventory',
    category: 'specialized',
    subcategory: 'sales',
    badge: 'selection-grade',
    shortDesc: 'გაყიდვების უნარის ტესტი — sales-სპეციფიკური',
    fullDesc: 'Sales-related personality და behavior pattern-ების შეფასება.',
    duration: '20 წთ',
    languages: ['ინგლისური'],
    forWho: 'გაყიდვების პოზიციები',
    publisher: 'Multiple',
    citation: 'Vinchur, A. J. et al. (1998). Journal of Applied Psychology, 83(4), 586-597.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'caliper',
    name: 'Caliper Profile',
    nameEn: 'Caliper Profile',
    category: 'specialized',
    subcategory: 'sales',
    badge: 'selection-grade',
    shortDesc: 'Sales-სპეციფიკური personality + cognitive ბატარეა',
    fullDesc: '60+ წლიანი ისტორია gas/sales სელექციაში.',
    duration: '60 წთ',
    languages: ['ინგლისური'],
    forWho: 'Sales executives, managers',
    publisher: 'Caliper',
    citation: 'Caliper Corporation (2020). Caliper Profile Technical Manual.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'csap',
    name: 'Customer Service Aptitude Profile (CSAP)',
    nameEn: 'Customer Service Aptitude Profile',
    category: 'specialized',
    subcategory: 'customer-service',
    badge: 'validated',
    shortDesc: 'კლიენტთან მომსახურების უნარი',
    fullDesc: 'Service-orientation, conflict resolution, empathy.',
    duration: '20 წთ',
    languages: ['ინგლისური'],
    forWho: 'Customer service, call center, რითეილი',
    publisher: 'Multiple',
    citation: 'Frei, R. L., & McDaniel, M. A. (1998). Human Performance, 11(1), 1-27.',
    warnings: [],
    canUseInSelection: true,
  },
  {
    key: 'pss',
    name: 'Perceived Stress Scale (PSS)',
    nameEn: 'Perceived Stress Scale',
    category: 'specialized',
    subcategory: 'stress',
    badge: 'organizational',
    shortDesc: 'სტრესის აღქმის ტესტი — Cohen-ის კლასიკა',
    fullDesc: '10 კითხვა. სტრესის სუბიექტური აღქმის გაზომვა.',
    duration: '5 წთ',
    languages: ['ინგლისური', 'რუსული'],
    forWho: 'Wellness program-ები',
    publisher: 'Free academic use',
    citation: 'Cohen, S. et al. (1983). Journal of Health and Social Behavior, 24(4), 385-396.',
    warnings: [],
    canUseInSelection: false,
  },
];

// Derived helpers
export const SELECTABLE_ASSESSMENTS = ASSESSMENTS_CATALOG.filter((a) => a.canUseInSelection);

export const ASSESSMENTS_BY_CATEGORY = ASSESSMENTS_CATALOG.reduce<
  Record<AssessmentCategory, Assessment[]>
>(
  (acc, a) => {
    if (!acc[a.category]) acc[a.category] = [];
    acc[a.category].push(a);
    return acc;
  },
  {} as Record<AssessmentCategory, Assessment[]>,
);

export const ASSESSMENT_SUBCATEGORIES: Record<AssessmentCategory, string[]> = {
  selection: ['cognitive', 'personality', 'integrity', 'practical', 'language'],
  development: ['self-awareness', 'leadership', 'emotional-intelligence'],
  organizational: ['engagement', 'burnout', 'satisfaction', 'culture', 'team-dynamics', '360'],
  specialized: ['sales', 'customer-service', 'stress', 'professional-knowledge'],
};

export function getAssessmentByKey(key: string): Assessment | undefined {
  return ASSESSMENTS_CATALOG.find((a) => a.key === key);
}
